// /penrose/commands: the full, searchable Penrose command library (moved off the
// landing so the landing stays clean).
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, ArrowLeft } from 'lucide-react';
import { PENROSE_COMMANDS } from '../data/penroseCommands';

const CATEGORY_ORDER = ['Moderation', 'Automod', 'Community', 'Setups', 'Utility', 'Tools', 'Information', 'Fun', 'Economy', 'Giveaway', 'Images', 'Socials', 'PremiumCommands'];

export default function PenroseCommands() {
  const [q, setQ] = useState('');
  const total = useMemo(() => PENROSE_COMMANDS.reduce((n, c) => n + c.commands.length, 0), []);
  const ordered = useMemo(
    () => [...PENROSE_COMMANDS].sort(
      (a, b) => (CATEGORY_ORDER.indexOf(a.category) + 1 || 99) - (CATEGORY_ORDER.indexOf(b.category) + 1 || 99)
    ),
    []
  );
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return ordered;
    return ordered
      .map((c) => ({ ...c, commands: c.commands.filter((cmd) => cmd.name.toLowerCase().includes(s) || cmd.description.toLowerCase().includes(s)) }))
      .filter((c) => c.commands.length > 0);
  }, [q, ordered]);

  return (
    <div className="space-y-8">
      <div>
        <Link to="/penrose" className="inline-flex items-center gap-1.5 text-sm text-textMuted transition-colors hover:text-textPrimary">
          <ArrowLeft className="h-4 w-4" />
          Penrose
        </Link>
        <div className="mt-3 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Penrose Commands</h1>
            <p className="mt-1 text-sm text-textMuted">{total} commands across {PENROSE_COMMANDS.length} categories.</p>
          </div>
          <div className="relative sm:w-72">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-textMuted" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search commands…" className="glass-input w-full py-2 pl-9 pr-3 text-sm" />
          </div>
        </div>
      </div>

      {filtered.length === 0 ? (
        <p className="py-10 text-center text-sm text-textMuted">No commands match "{q}".</p>
      ) : (
        <div className="space-y-8">
          {filtered.map((cat) => (
            <div key={cat.category}>
              <div className="mb-3 flex items-center gap-2">
                <span className="h-4 w-0.5 rounded-full bg-accent" />
                <h2 className="text-sm font-bold uppercase tracking-widest text-textSecondary">{cat.category}</h2>
                <span className="text-xs text-textMuted">{cat.commands.length}</span>
              </div>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {cat.commands.map((cmd) => (
                  <div key={cmd.name} className="glass-panel p-3">
                    <code className="text-[13px] font-semibold text-accent">/{cmd.name}</code>
                    {cmd.description && <p className="mt-1 text-xs leading-relaxed text-textSecondary">{cmd.description}</p>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
